import { useState, useEffect, useCallback, useMemo } from 'react';
import { ArrowRight, ArrowLeft, Loader2, CheckCircle, AlertCircle, X } from 'lucide-react';
import { apiService } from '../../services/api.service';
import { setTokens } from '../../services/auth.service';
import { API_ENDPOINTS } from '../../config/environment';
import { ReactComponent as HizliyoLogo } from '../../assets/hizliyo-logo.svg';
import './LoginPage.css';

const decodeToken = (token) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
        const decoded = JSON.parse(jsonPayload);
        const role = decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || decoded.role || null;
        return { ...decoded, role };
    } catch { return null; }
};

const formatPhone = (raw) => {
    const d = raw.replace(/\D/g, '');
    if (!d) return '';
    if (d.length <= 3) return `(${d}`;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    if (d.length <= 8) return `(${d.slice(0, 3)}) ${d.slice(3, 6)} ${d.slice(6)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)} ${d.slice(6, 8)} ${d.slice(8, 10)}`;
};

const cleanPhone = (masked) => '+90' + masked.replace(/\D/g, '');

const stripCountryCode = (raw) => {
    let d = raw.replace(/\D/g, '');
    if (d.startsWith('90') && d.length > 10) d = d.slice(2);
    if (d.startsWith('0') && d.length > 10) d = d.slice(1);
    return d.slice(0, 10);
};

const GREETINGS = [
    { range: [5, 12], text: 'Günaydın', emoji: '\u{1F305}' },
    { range: [12, 18], text: 'İyi günler', emoji: '\u{2600}\u{FE0F}' },
    { range: [18, 22], text: 'İyi akşamlar', emoji: '\u{1F307}' },
    { range: [22, 5], text: 'İyi geceler', emoji: '\u{1F319}' },
];

const MOTIVATIONS = [
    'Bugün harika teslimatlar seni bekliyor!',
    'Her teslimat bir gülümseme demek!',
    'Filonun yıldızı olmaya hazır mısın?',
    'Hız seninle başlar!',
    'Bugün rekor kırma günü!',
    'Yollara hükmetmeye hazır mısın?',
    'Teslimatlar seni bekliyor, patron!',
];

function getGreeting() {
    const h = new Date().getHours();
    const g = GREETINGS.find(g => {
        if (g.range[0] < g.range[1]) return h >= g.range[0] && h < g.range[1];
        return h >= g.range[0] || h < g.range[1];
    });
    return g || GREETINGS[1];
}

function Toast({ message, type, onClose }) {
    useEffect(() => {
        const t = setTimeout(onClose, 5000);
        return () => clearTimeout(t);
    }, [onClose]);

    return (
        <div className={`fl-toast fl-toast-${type}`}>
            {type === 'error' ? <AlertCircle size={18} /> : <CheckCircle size={18} />}
            <span>{message}</span>
            <button className="fl-toast-close" onClick={onClose}><X size={14} /></button>
        </div>
    );
}

function LoginPage({ onLoginSuccess }) {
    const [toast, setToast] = useState(null);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [isCodeSent, setIsCodeSent] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [loading, setLoading] = useState(false);

    const greeting = useMemo(() => getGreeting(), []);
    const motivation = useMemo(() => MOTIVATIONS[Math.floor(Math.random() * MOTIVATIONS.length)], []);

    const showToast = useCallback((message, type = 'error') => setToast({ message, type, key: Date.now() }), []);

    useEffect(() => {
        if (countdown > 0) {
            const t = setTimeout(() => setCountdown(c => c - 1), 1000);
            return () => clearTimeout(t);
        }
    }, [countdown]);

    const handlePhoneInput = (e) => {
        const digits = stripCountryCode(e.target.value);
        setPhoneNumber(formatPhone(digits));
    };

    const handleRequestCode = async () => {
        const digits = phoneNumber.replace(/\D/g, '');
        if (digits.length !== 10) { showToast('Geçerli bir telefon numarası girin'); return; }
        try {
            setLoading(true);
            const result = await apiService.post(API_ENDPOINTS.AUTH.REQUEST_CODE, { phoneNumber: cleanPhone(phoneNumber), tenantIdentifier: 'fleet' });
            if (result.success) {
                setIsCodeSent(true);
                setCountdown(60);
            } else {
                showToast(result.errors?.[0] || result.error || 'Kod gönderilemedi');
            }
        } catch (err) {
            showToast('Bağlantı hatası');
        } finally { setLoading(false); }
    };

    const handleResendCode = async () => {
        if (countdown > 0) return;
        try {
            setLoading(true);
            const result = await apiService.post(API_ENDPOINTS.AUTH.RESEND_CODE, { phoneNumber: cleanPhone(phoneNumber) });
            if (result.success) {
                setCountdown(60);
                showToast('Kod tekrar gönderildi', 'success');
            } else {
                showToast(result.errors?.[0] || result.error || 'Kod gönderilemedi');
            }
        } catch { showToast('Bağlantı hatası'); }
        finally { setLoading(false); }
    };

    const handleVerifyCode = async () => {
        if (!verificationCode.trim()) { showToast('Doğrulama kodunu girin'); return; }
        try {
            setLoading(true);
            const result = await apiService.post(API_ENDPOINTS.AUTH.VERIFY_CODE, { phoneNumber: cleanPhone(phoneNumber), code: verificationCode, tenantIdentifier: 'fleet' });
            if (result.success && result.data?.accessToken) {
                const token = result.data.accessToken;
                const decoded = decodeToken(token);
                if (!decoded || decoded.role !== 'Fleet') { showToast('Bu uygulamaya sadece filo kullanıcıları erişebilir'); return; }
                setTokens(token, result.data.refreshToken);
                onLoginSuccess?.();
            } else {
                showToast(result.errors?.[0] || result.error || 'Doğrulama başarısız');
            }
        } catch { showToast('Bağlantı hatası'); }
        finally { setLoading(false); }
    };

    return (
        <div className="fl-page">
            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} key={toast.key} />}

            <div className="fl-bg">
                <div className="fl-glow fl-glow-1" />
                <div className="fl-glow fl-glow-2" />
                <div className="fl-grid-pattern" />
            </div>

            <div className="fl-wrapper">
                <div className="fl-brand">
                    <HizliyoLogo className="fl-logo" />
                    <div className="fl-badge">FİLO</div>
                </div>

                <div className="fl-motivation-banner">
                    <span>{motivation}</span>
                </div>

                <div className="fl-card">
                    <div className="fl-card-header">
                        <div className="fl-status-dot" />
                        <span>Filo Yönetim Paneli</span>
                    </div>

                    <div className="fl-body">
                        {!isCodeSent ? (
                            <div className="fl-form fl-fade">
                                <div className="fl-heading">
                                    <div className="fl-greeting-row">
                                        <h2>{greeting.text}</h2>
                                        <div className="fl-wave-hand">{greeting.emoji}</div>
                                    </div>
                                    <p>Telefon numaranızla giriş yapın</p>
                                </div>
                                <div className="fl-input-wrap">
                                    <span className="fl-prefix">+90</span>
                                    <input
                                        type="tel" className="fl-input fl-input-phone" placeholder="(5XX) XXX XX XX"
                                        value={phoneNumber} onChange={handlePhoneInput}
                                        onKeyDown={(e) => e.key === 'Enter' && handleRequestCode()}
                                        disabled={loading} inputMode="tel" autoFocus
                                    />
                                </div>
                                <button className="fl-btn" onClick={handleRequestCode} disabled={loading || phoneNumber.replace(/\D/g, '').length !== 10}>
                                    {loading ? <Loader2 size={20} className="fl-spin" /> : <><span>Kod Gönder</span><ArrowRight size={18} /></>}
                                </button>
                            </div>
                        ) : (
                            <div className="fl-form fl-fade">
                                <div className="fl-heading">
                                    <div className="fl-greeting-row">
                                        <h2>Doğrulama Kodu</h2>
                                        <div className="fl-wave-hand">{'\u{1F510}'}</div>
                                    </div>
                                    <p><strong>+90 {phoneNumber}</strong> numarasına gönderilen 6 haneli kodu girin</p>
                                </div>
                                <button className="fl-back" onClick={() => { setIsCodeSent(false); setVerificationCode(''); setCountdown(0); }}>
                                    <ArrowLeft size={16} /> Numarayı Değiştir
                                </button>
                                <div className="fl-otp-wrap">
                                    <input
                                        type="text" className="fl-input fl-input-otp" placeholder="------"
                                        value={verificationCode} maxLength={6}
                                        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                                        onKeyDown={(e) => e.key === 'Enter' && handleVerifyCode()}
                                        disabled={loading} autoFocus inputMode="numeric"
                                    />
                                </div>
                                <button className="fl-btn" onClick={handleVerifyCode} disabled={loading || verificationCode.length < 6}>
                                    {loading ? <Loader2 size={20} className="fl-spin" /> : <><span>Giriş Yap</span><ArrowRight size={18} /></>}
                                </button>
                                <button className="fl-link" onClick={handleResendCode} disabled={countdown > 0 || loading}>
                                    {countdown > 0 ? `Tekrar gönder (${countdown}s)` : 'Kodu Tekrar Gönder'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                <p className="fl-footer">Hızlıyo Filo Yönetim Sistemi</p>
            </div>
        </div>
    );
}

export default LoginPage;
